/**
 * Conversions entre la durée saisie au formulaire — des heures et des minutes,
 * dans deux champs — et les minutes stockées par une formule.
 *
 * Un `<input type="time"> ` serait plus court à écrire mais plafonne à 23:59 :
 * il rendait inéditables les formations de plusieurs jours (CSE 35H, formation
 * de formateur incendie 28H), et enregistrer leur fiche effaçait leur formule.
 */

/**
 * CE QUI S'APPUIE SUR UNE FORMATION quand on n'en sait rien : aucune session
 * inter programmée, aucun devis vendu. Le composable des formules le pose à
 * chaque changement de formation, l'onglet Tarifs et la grille le prennent
 * par défaut ; une seule écriture, gelée pour qu'aucun d'eux ne la modifie
 * sous les autres.
 */
export const USAGES_VIDES = Object.freeze({ sessionsInter: 0, devis: 0 })

/** 2100 → `{ heures: 35, minutes: 0 }`. Une durée qui n'en est pas une rend des champs vides. */
export function minutesVersHM(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return { heures: '', minutes: '' }
  return { heures: Math.floor(minutes / 60), minutes: minutes % 60 }
}

/** `35`, `0` → 2100. Rend 0 si la saisie est vide ou illisible. */
export function hmVersMinutes(heures, minutes) {
  const h = Number(heures)
  const m = Number(minutes)
  const total = (Number.isFinite(h) && h > 0 ? Math.floor(h) : 0) * 60
    + (Number.isFinite(m) && m > 0 ? Math.floor(m) : 0)
  return total > 0 ? total : 0
}

/** 210 → « 3H30 », l'écriture du catalogue. */
export function minutesVersLibelle(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return ''
  return `${Math.floor(minutes / 60)}H${String(minutes % 60).padStart(2, '0')}`
}

/**
 * LES FORMATS DE LA GRILLE « PAR SESSIONS », au choix plutôt qu'en heures — le
 * pendant de `FORMATS_SESSIONS` côté serveur (formation-formules.service.js).
 * En ajouter un ici impose de l'ajouter là-bas, qui refuse les autres.
 */
export const FORMATS_SESSIONS = Object.freeze([
  { dureeMinutes: 210, libelle: '1/2 journée' },
  { dureeMinutes: 420, libelle: '1 journée' }
])

/** « 1 journée », ou la durée en heures si elle n'est pas un format de la liste. */
export function libelleFormat(dureeMinutes) {
  return FORMATS_SESSIONS.find((f) => f.dureeMinutes === dureeMinutes)?.libelle
    || minutesVersLibelle(dureeMinutes)
}

/**
 * Une formule du tableau « Par journée » : elle porte son nombre de sessions.
 * Le pendant de `estEnSessions` côté serveur. (Le code dit « sessions » pour
 * ce mode, l'écran dit « Par journée » — cf. `MODES_TARIF`.)
 */
export const estEnSessions = (formule) => Number(formule?.nbSessions) > 0

/** Une formation vendue « Par journée » : ses formules portent leurs sessions. */
export const venduParSessions = (formules) => (formules || []).some(estEnSessions)

/** « 1 session », « 6 sessions ». */
export const libelleSessions = (nombre) => `${nombre} session${nombre > 1 ? 's' : ''}`

/**
 * Les formules d'une formation par sessions, dans l'ordre où on les compare :
 * du plus court au plus long, puis de la plus petite capacité à la plus
 * grande.
 */
export function formulesEnSessions(formules) {
  return (formules || [])
    .filter((f) => estEnSessions(f) && (f.nbGroupes || 1) === 1)
    .sort((a, b) => a.dureeMinutes - b.dureeMinutes || a.participantsMax - b.participantsMax)
}

/**
 * La formule qui accueille un effectif au moindre coût : la moins chère de
 * celles dont la capacité suffit. `null` quand aucune ne suffit — au-delà de
 * la plus grande capacité, la demande se chiffre à la main.
 */
export function formuleEnSessionsPourEffectif(formules, effectif) {
  const personnes = Number(effectif)
  if (!Number.isFinite(personnes) || personnes < 1) return null
  const assez = formulesEnSessions(formules).filter((f) => f.participantsMax >= personnes)
  if (!assez.length) return null
  return assez.reduce((moins, f) => (Number(f.prix) < Number(moins.prix) ? f : moins))
}

/** « 1 journée · jusqu'à 36 personnes » — ce qu'une formule par journée vend. */
export const libelleFormuleEnSessions = (formule) => (
  `${libelleFormat(formule.dureeMinutes)} · jusqu’à ${formule.participantsMax} personnes`
)

/**
 * Tarification par lots, partagée par le formulaire public et la fiche
 * demande : les deux doivent annoncer le même prix pour la même demande.
 */

/**
 * LA TRANCHE SANS LIMITE — le pendant de `PARTICIPANTS_SANS_LIMITE` côté
 * serveur (formation-formules.service.js). Une formation qu'on ne borne pas
 * s'enregistre avec une borne haute assez large pour que toute demande y
 * entre, et s'affiche « Tout le personnel » plutôt que « 1 à 999 ».
 */
export const PARTICIPANTS_SANS_LIMITE = 999
export const LIBELLE_SANS_LIMITE = 'Tout le personnel'

export const trancheSansLimite = ({ min, max, participantsMin, participantsMax } = {}) => {
  const bas = min ?? participantsMin
  const haut = max ?? participantsMax
  return Number(bas) <= 1 && Number(haut) >= PARTICIPANTS_SANS_LIMITE
}

const rempliTranche = (v) => v !== '' && v !== null && v !== undefined

/** Les deux bornes vides : la colonne ne borne plus la taille du groupe. */
export const trancheVide = (tranche) => !rempliTranche(tranche.min) && !rempliTranche(tranche.max)

/** « 4 à 12 participants », « jusqu'à 12 participants », ou « Tout le personnel ». */
export function libelleTranche(tranche) {
  if (trancheVide(tranche) || trancheSansLimite(tranche)) return LIBELLE_SANS_LIMITE
  if (!rempliTranche(tranche.max) || tranche.min === tranche.max) return `${tranche.min} participants`
  if (!rempliTranche(tranche.min)) return `jusqu’à ${tranche.max} participants`
  return `${tranche.min} à ${tranche.max} participants`
}

/** Ce qu'on accole à la durée et à la taille de groupe d'une case retirée. */
export const MENTION_RETIREE = '(retirée de la grille)'

/**
 * Les formules d'une formation vues depuis un devis : la grille proposée, plus
 * la formule du devis quand elle n'y est plus — retirée, prix vidé, formation
 * passée sur devis. Sans elle, la fiche afficherait la durée du catalogue au
 * lieu de celle qui a été vendue. Une formule d'une autre formation n'est pas
 * reprise : changer de formation sur la fiche ne doit pas proposer l'ancienne.
 */
export function formulesDuDevis(formation, quote) {
  const grille = formation?.FORMULES || []
  const vendue = quote?.formation_formule
  if (!vendue || vendue.formationId !== formation?.ID) return grille
  if (grille.some((f) => f.id === vendue.id)) return grille
  return [...grille, vendue]
}

/** Identifie une taille de groupe : « 1-12 ». */
export const cleTranche = (formule) => `${formule.participantsMin}-${formule.participantsMax}`

/** Les tailles de groupe vendues à une durée, dans l'ordre, sans doublon. */
export function tranchesDeLaDuree(formules, dureeMinutes) {
  const vues = new Map()
  for (const formule of formules) {
    if (formule.dureeMinutes !== dureeMinutes) continue
    const cle = cleTranche(formule)
    if (!vues.has(cle)) {
      vues.set(cle, { cle, min: formule.participantsMin, max: formule.participantsMax })
    }
  }
  return [...vues.values()].sort((a, b) => a.min - b.min)
}

/**
 * Combien de groupes pour un effectif donné, en groupes de `parGroupe` au
 * plus : vingt-cinq personnes par douze, c'est trois groupes.
 */
export function groupesNecessaires(effectif, parGroupe) {
  const total = Number(effectif)
  const taille = Number(parGroupe)
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(taille) || taille <= 0) return 1
  return Math.max(1, Math.ceil(total / taille))
}

/**
 * Le palier qui s'applique à une demande : celui du nombre de groupes voulu,
 * ou le plus élevé en dessous.
 *
 * Le tarif étant dégressif, le palier le plus élevé est le moins cher. Une
 * demande de dix groupes sur un barème qui s'arrête à quatre est donc
 * facturée dix fois le tarif « quatre groupes » : on ne facture jamais moins
 * de groupes qu'il n'en faut, et une commande plus grosse que le barème n'est
 * pas punie pour autant.
 */
export function palierApplicable(formules, { dureeMinutes, tranche, nbGroupes }) {
  const voulus = Number(nbGroupes)
  if (!tranche || !Number.isFinite(voulus) || voulus < 1) return null

  const duLot = formules
    .filter((f) => f.dureeMinutes === dureeMinutes && cleTranche(f) === tranche)
    .sort((a, b) => (a.nbGroupes || 1) - (b.nbGroupes || 1))
  if (!duLot.length) return null

  const eligibles = duLot.filter((f) => (f.nbGroupes || 1) <= voulus)
  return eligibles.length ? eligibles[eligibles.length - 1] : duLot[0]
}

/** Ce que coûte une intervention intra : le prix du palier, multiplié par les groupes. */
export function totalDesGroupes(palier, nbGroupes) {
  const groupes = Number(nbGroupes)
  if (!palier || palier.prix === null || palier.prix === undefined) return null
  if (!Number.isFinite(groupes) || groupes < 1) return null
  return Number(palier.prix) * groupes
}

/**
 * Ce que le barème dégressif fait gagner sur une commande de plusieurs
 * groupes : le prix d'un groupe commandé seul, celui du palier retenu, et
 * l'écart par groupe et au total.
 *
 * `null` quand rien ne baisse : palier « un groupe » absent ou sans prix, ou
 * barème qui ne descend pas — la saisie ne garantit pas la décroissance,
 * seul l'écart réel compte.
 */
export function remiseDegressive(formules, { dureeMinutes, palier, nbGroupes }) {
  const groupes = Number(nbGroupes)
  if (!palier || palier.prix === null || palier.prix === undefined) return null
  if (!Number.isFinite(groupes) || groupes < 2) return null

  const seul = palierApplicable(formules, { dureeMinutes, tranche: cleTranche(palier), nbGroupes: 1 })
  if (!seul || (seul.nbGroupes || 1) !== 1 || seul.prix === null || seul.prix === undefined) return null

  const avant = Number(seul.prix)
  const apres = Number(palier.prix)
  if (!(avant > apres)) return null
  return { avant, apres, parGroupe: avant - apres, total: (avant - apres) * groupes }
}

/**
 * Le tarif inter d'une durée, au stagiaire.
 *
 * Il se vend à la place et vaut pour toute la ligne du barème : le catalogue
 * n'en saisit qu'un par durée, puis le recopie sur chaque case à un groupe
 * pour qu'il reste trouvable quelle que soit la formule retenue. La tranche
 * d'effectif n'entre donc pas dans le calcul — la chercher par formule
 * donnerait `null` dès que la formule retenue porte plusieurs groupes.
 */
export function prixInterDeLaDuree(formules, dureeMinutes) {
  const ligne = (formules || []).find((f) => (
    f.dureeMinutes === dureeMinutes && f.prixInter !== null && f.prixInter !== undefined
  ))
  return ligne ? Number(ligne.prixInter) : null
}

/** Ce que coûte une inscription en inter : le tarif à la place × l'effectif. */
export function totalParPersonne(prixUnitaire, effectif) {
  const personnes = Number(effectif)
  const unitaire = Number(prixUnitaire)
  if (!Number.isFinite(unitaire) || unitaire <= 0) return null
  if (!Number.isFinite(personnes) || personnes < 1) return null
  return unitaire * personnes
}

export function totalDeLaDemande(formules, { lieu, dureeMinutes, palier, effectif, nbGroupes }) {
  return lieu === 'INTER'
    ? totalParPersonne(prixInterDeLaDuree(formules, dureeMinutes), effectif)
    : totalDesGroupes(palier, nbGroupes)
}
